import { getCurrencySymbol } from '@vendasta/galaxy/utility/currency-utils';
import { getLocaleCode } from '@vendasta/galaxy/utility/locale';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'glxyCurrency',
  standalone: false,
})
export class GalaxyCurrencyPipe implements PipeTransform {
  /**
   * @param value {string} the numerical value that is being converted to a currency string
   * @param currencyCode {string} Three character code to represent the currency (ISO 4217) ex) USD, CAD, AUS
   * @param locale {string} A locale code for the locale format rules to use. (ISO 639-1 w/ optional two character region subtag) ex) en, en-US, en-CA, fr-CA, fr
   * @returns {string} Returns a string of the localized currency value with the currency symbol for the given currency code. Includes currency code at the end if currency symbol is "$"
   */
  transform(value: number, currencyCode = 'USD', locale?: string): string {
    if (!value && value !== 0) {
      return '';
    }

    if (!locale) {
      locale = getLocaleCode();
    }

    let currencyString;
    try {
      currencyString = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      }).format(value);
    } catch (err) {
      currencyString = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
      }).format(value);
    }

    const currencySymbol = getCurrencySymbol(currencyCode) || '$';

    // sometimes the Number format doesn't return a string with a currency symbol. Instead, it returns the currency code.
    // if that's the case we'll get currency symbol and replace the currency code with the symbol.
    // ex) currency is "CZK", number format returns "CZK 3", so we swap out the currency code for the symbol (Kč)
    if (currencyString.includes(currencyCode)) {
      currencyString = currencyString.replace(currencyCode, currencySymbol);
    }

    // we want to the currency code to the end of the string if the currency symbol is "$"
    // this is b/c most countries that use the "$" as their currency symbol hold different monetary values and each refer to a different currency code. ex) CAD vs USD
    // in contrast, the currency symbol of the Euro (€) has the same value across all countries that use the "€" symbol b/c they are all refering to the same currency code (EUR).
    if (currencySymbol === '$') {
      currencyString += ` ${currencyCode}`;
    }

    return currencyString;
  }
}
